import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { PiPlayLight, PiWarningCircleLight, PiXLight } from 'react-icons/pi';
import Button from '../shared/TailwindComponents/Button';
import FileUploader from '../shared/TailwindComponents/FileUploader';
import FieldShell, { FIELD_HELP_CLASS } from '../shared/TailwindComponents/FieldShell';
import {
  PLAYBOOK_MAX_FILES,
  PLAYBOOK_MAX_FILE_BYTES,
  PLAYBOOK_SOURCE_EXTENSIONS,
  StageRejection,
  StagedPlaybookFile,
  stagePlaybookFiles,
} from './playbookExtraction';

/**
 * The staging half of playbook generation.
 *
 * A playbook is rarely one document — a firm's standards live across a
 * negotiation guide, a clause bank, a jurisdiction annex and whatever was
 * scanned years ago. So the zone takes many, and nothing is sent until the
 * user presses generate: extraction is long, one-shot, and rewrites the
 * playbook's rules, which makes "oops, wrong file" expensive.
 *
 * Controlled, so `BaseForm` can drive it as a custom field on the create form
 * and the detail page can drive it from its own state.
 */

export interface PlaybookSourceUploadProps {
  value?: StagedPlaybookFile[];
  onChange: (files: StagedPlaybookFile[]) => void;
  /** Renders the generate button. Omit when a surrounding form submits. */
  onSubmit?: (files: File[]) => void;
  submitLabel?: string;
  isSubmitting?: boolean;
  disabled?: boolean;
  /** The field label, drawn by `FieldShell`. Omit when a heading already names it. */
  label?: string;
  /** Validation verdict from a surrounding form, shown in the shell's error row. */
  error?: string;
  /** Accepted for call-site compatibility; the shared uploader has one look. */
  density?: 'canvas' | 'pane';
  className?: string;
  'data-tour-id'?: string;
}

const PlaybookSourceUpload: React.FC<PlaybookSourceUploadProps> = ({
  value = [],
  onChange,
  onSubmit,
  submitLabel,
  isSubmitting = false,
  disabled = false,
  label,
  error,
  className,
  'data-tour-id': dataTourId,
}) => {
  const { t } = useTranslation();
  const [rejections, setRejections] = useState<StageRejection[]>([]);

  /**
   * `FileUploader` owns the whole accepted set, so its callback hands back the
   * list AFTER its own security pass (extension whitelist, size cap,
   * magic-byte/double-extension checks) and after any removal it made. Re-stage
   * from that list rather than diffing: staging stays the single place that
   * decides identity, and duplicates the security layer has no opinion about
   * (same name + size dropped twice) are still refused here.
   */
  const handleFilesChange = useCallback(
    (files: File[]) => {
      const { staged, rejected } = stagePlaybookFiles([], files, PLAYBOOK_MAX_FILES);
      setRejections(rejected);
      onChange(staged);
    },
    [onChange],
  );

  const stagedFiles = value.map((staged) => staged.file);

  return (
    <div className={clsx('space-y-3', className)} data-tour-id={dataTourId}>
      {/*
        The label and the budget line are the field's own chrome, so they come
        from the shell every other control uses rather than from a `<p>` that
        happened to be `text-sm`/`text-[11px]`.
      */}
      <FieldShell
        label={label}
        description={t('Up to {{max}} files · 50MB each · documents, spreadsheets and scans', {
          max: PLAYBOOK_MAX_FILES,
        })}
        error={error}
        translate={false}
      >
        {/*
          The shared uploader, so playbook sources go through the same file
          security gate as every other upload in the app. Nothing is sent on
          selection — the files sit in the staging list until generate.
        */}
        <fieldset disabled={disabled || isSubmitting} className="contents">
          <FileUploader
            files={stagedFiles}
            onFilesChange={handleFilesChange}
            allowedExtensions={PLAYBOOK_SOURCE_EXTENSIONS}
            maxSizeBytes={PLAYBOOK_MAX_FILE_BYTES}
            maxFiles={PLAYBOOK_MAX_FILES}
          />
        </fieldset>
      </FieldShell>

      {rejections.length > 0 && (
        <div className="flex items-start gap-2 rounded-md border border-warning-200/60 dark:border-warning-800/60 bg-warning-50/60 dark:bg-warning-900/20 px-2.5 py-2">
          <PiWarningCircleLight className="h-4 w-4 text-warning-500 shrink-0 mt-0.5" />
          <ul className="flex-1 min-w-0 space-y-0.5">
            {rejections.map((rejection) => (
              <li
                key={`${rejection.reason}:${rejection.name}`}
                className="text-xs text-warning-700 dark:text-warning-300 break-words"
              >
                {rejection.reason === 'duplicate'
                  ? t('{{name}} is already in the list', { name: rejection.name })
                  : t('{{name}} was not added — a playbook takes at most {{max}} documents', {
                      name: rejection.name,
                      max: PLAYBOOK_MAX_FILES,
                    })}
              </li>
            ))}
          </ul>
          <button
            type="button"
            aria-label={t('Dismiss')}
            onClick={() => setRejections([])}
            className="p-1 rounded-md text-warning-600 hover:text-warning-800 transition-colors"
          >
            <PiXLight className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {/*
        The uploader renders the staged files themselves (chip per file,
        remove, clear all), so all that is left here is the count — the line
        that tells the user how much of the 20-document budget is spent.
      */}
      {value.length > 0 && (
        <p className={FIELD_HELP_CLASS}>
          {value.length === 1
            ? t('1 document staged')
            : t('{{count}} documents staged', { count: value.length })}
        </p>
      )}

      {onSubmit && value.length > 0 && (
        <div className="flex justify-end">
          <Button
            variant="primary"
            size="small"
            icon={<PiPlayLight className="w-4 h-4" />}
            disabled={disabled || isSubmitting}
            isLoading={isSubmitting}
            onClick={() => onSubmit(value.map((s) => s.file))}
          >
            {submitLabel || t('Generate playbook')}
          </Button>
        </div>
      )}
    </div>
  );
};

export default PlaybookSourceUpload;
